// src/components/Header/Header.jsx
import React, {
   useState,
   useContext,
   useEffect,
   useMemo,
   useCallback,
} from "react";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { openPopup as openPopupUI } from "../Common/popupUIStore";

import crownIcon from "../../assets/svg/crown.svg";
import wrenchIcon from "../../assets/svg/wrench.svg";
import studentIcon from "../../assets/svg/graduate.svg";

import DarkModeToggle from "./DarkModeToggle";
import SettingsButton from "./SettingsButton";
import { setStudentAppearance } from "../../utils/appearanceSettings";
import { UserContext } from "../../UserContext";
import useAdminReviewsUnread from "../../hooks/useAdminReviewsUnread";
import useNotificationsUnread from "../../hooks/useNotificationsUnread";
import useChatUnread from "../../hooks/useChatUnread";
import useMessagesUnreadBadge from "../../hooks/useMessagesUnreadBadge";
import { getInstructors } from "../../api/instructorsService";
import { normalizeRole, ROOT_ROLE_LINKS, ROLES } from "../../auth/access";

import { getLinksForRole, getMoreLinksForRole } from "./navLinks";

/* ===================== helpers ===================== */
function useIsMobile(bp = 992) {
   const [isMobile, setIsMobile] = useState(() => {
      if (typeof window === "undefined") return false;
      return window.matchMedia(`(max-width:${bp}px)`).matches;
   });

   useEffect(() => {
      if (typeof window === "undefined") return;
      const m = window.matchMedia(`(max-width:${bp}px)`);
      const onChange = () => setIsMobile(m.matches);

      if (m.addEventListener) m.addEventListener("change", onChange);
      else m.addListener(onChange);

      onChange();
      return () => {
         if (m.removeEventListener) m.removeEventListener("change", onChange);
         else m.removeListener(onChange);
      };
   }, [bp]);

   return isMobile;
}

const isRootLink = (link) => ROOT_ROLE_LINKS.includes(link);

/* ===================== component ===================== */
const Header = ({ children }) => {
   const { user } = useContext(UserContext);
   const normalizedRole = normalizeRole(user?.role);
   // setările de aspect (culoare, text, animații) sunt doar pentru elevi
   const isStudent = normalizedRole === ROLES.USER;
   useEffect(() => {
      if (user) setStudentAppearance(isStudent);
   }, [user, isStudent]);
   const [currentInstructor, setCurrentInstructor] = useState(null);

   // ✅ links vin din Header, nu din pagini
   const links = useMemo(
      () =>
         getLinksForRole(normalizedRole, {
            instructor: currentInstructor,
         }),
      [normalizedRole, currentInstructor],
   );
   const moreLinks = useMemo(
      () => getMoreLinksForRole(normalizedRole),
      [normalizedRole],
   );

   // „Altele" schimbă meniul pe loc cu paginile rar folosite. Dacă pagina
   // curentă e una dintre ele (ex. după refresh), pornim direct din acel meniu.
   const { pathname } = useLocation();
   const [menuSection, setMenuSection] = useState(() =>
      moreLinks.some((item) => item.link && pathname.startsWith(item.link))
         ? "more"
         : "main",
   );
   // Animăm lista doar după o comutare făcută de utilizator, nu la încărcare.
   const [sectionSwitched, setSectionSwitched] = useState(false);
   const switchSection = useCallback((next) => {
      setSectionSwitched(true);
      setMenuSection(next);
   }, []);
   const showMore = menuSection === "more" && moreLinks.length > 0;
   const sectionClass =
      (sectionSwitched ? " menu__section" : "") +
      (showMore ? " menu__section--more" : "");
   const sectionLinks = showMore ? moreLinks : links;

   const baseMobileLinks = useMemo(() => {
      if (normalizedRole !== ROLES.USER) return links;

      return (links || []).filter((item) => {
         if (!item) return false;
         if (item.link === "/student/exam") return false;
         return true;
      });
   }, [links, normalizedRole]);
   const mobileLinks = showMore ? moreLinks : baseMobileLinks;

   const TABLET_BP = 992;
   const isMobile = useIsMobile(TABLET_BP);
   const [mobileOpen, setMobileOpen] = useState(false);

   const [displayName, setDisplayName] = useState({
      firstName: "",
      lastName: "",
   });

   let iconSrc = studentIcon;
   let roleLabel = "Student";

   if (normalizedRole === ROLES.ADMIN) {
      iconSrc = crownIcon;
      roleLabel = "Administrator";
   } else if (normalizedRole === ROLES.MANAGER) {
      iconSrc = wrenchIcon;
      roleLabel = "Manager";
   } else if (normalizedRole === ROLES.INSTRUCTOR) {
      iconSrc = wrenchIcon;
      roleLabel = "Instructor";
   } else if (normalizedRole === ROLES.PROFESSOR) {
      iconSrc = wrenchIcon;
      roleLabel = "Professor";
   }

   useEffect(() => {
      let cancelled = false;

      async function resolveName() {
         if (!user) {
            setDisplayName({ firstName: "", lastName: "" });
            setCurrentInstructor(null);
            return;
         }

         let firstName = user.firstName || "";
         let lastName = user.lastName || "";

         if (normalizedRole === ROLES.INSTRUCTOR) {
            try {
               const list = await getInstructors();
               const mine = list.find(
                  (i) => String(i.userId) === String(user.id),
               );
               if (!cancelled) setCurrentInstructor(mine || null);
               if (mine) {
                  firstName = mine.firstName || firstName || "";
                  lastName = mine.lastName || lastName || "";
               }
            } catch (e) {
               if (!cancelled) setCurrentInstructor(null);
            }
         } else {
            setCurrentInstructor(null);
         }

         if (!cancelled) setDisplayName({ firstName, lastName });
      }

      resolveName();
      return () => {
         cancelled = true;
      };
   }, [user, normalizedRole]);

   useEffect(() => {
      if (!isMobile && mobileOpen) setMobileOpen(false);
   }, [isMobile, mobileOpen]);

   // Bara de jos de pe mobil rămâne mereu cu primele linkuri principale.
   const primaryLinks = useMemo(() => {
      const source = isMobile ? baseMobileLinks : links;
      return (Array.isArray(source) ? source : []).slice(0, 4);
   }, [isMobile, links, baseMobileLinks]);

   const closeMobile = useCallback(() => setMobileOpen(false), []);
   const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);
   const openStudentProfile = useCallback(() => {
      if (normalizedRole !== ROLES.USER) return;
      openPopupUI("studentProfile");
   }, [normalizedRole]);

   const onNavItemClick = useCallback(() => {
      if (isMobile) closeMobile();
   }, [isMobile, closeMobile]);

   const { unreadCount: adminReviewsUnread } =
      useAdminReviewsUnread(currentInstructor);
   const { unreadCount: notificationsUnread } = useNotificationsUnread();
   const { hasUnread: chatUnread } = useChatUnread();
   const { unreadCount: messagesUnreadCount } = useMessagesUnreadBadge();

   const renderItem = (item, i) => {
      if (item.submenu || item.back) {
         return (
            <li
               key={`${item.text}-${i}`}
               className={
                  "menu__item" +
                  (item.back ? " menu__item--back" : " menu__item--submenu")
               }
            >
               <button
                  type="button"
                  className="menu__link"
                  onClick={() => switchSection(item.back ? "main" : "more")}
                  aria-expanded={item.submenu ? showMore : undefined}
               >
                  <ReactSVG className="menu__icon" src={item.icon} />
                  <p className="menu__nav-text">{item.text}</p>
               </button>
            </li>
         );
      }

      if (item.disabled) {
         return (
            <li
               key={`${item.text}-${i}`}
               className="menu__item menu__item--disabled"
            >
               <span
                  className="menu__link menu__link--disabled"
                  aria-disabled="true"
               >
                  <ReactSVG className="menu__icon" src={item.icon} />
                  <p className="menu__nav-text">{item.text}</p>
               </span>
            </li>
         );
      }

      if (item.popup) {
         let badgeCount = 0;
         let badgeDot = false;
         if (item.badgeKey === "adminReviews") badgeCount = adminReviewsUnread;
         else if (item.badgeKey === "notifications")
            badgeCount = notificationsUnread;
         else if (item.badgeKey === "staffChat") {
            if (messagesUnreadCount > 0) badgeCount = messagesUnreadCount;
            else if (chatUnread) badgeDot = true;
         }

         const isMessagesItem = item.badgeKey === "staffChat";

         return (
            <li
               key={`${item.text}-${i}`}
               className={
                  "menu__item" +
                  (isMessagesItem ? " menu__item--messages" : "")
               }
            >
               <button
                  type="button"
                  className={
                     "menu__link" +
                     (isMessagesItem ? " menu__link--messages" : "")
                  }
                  onClick={() => {
                     if (item.popup === "studentVideoLibrary")
                        openPopupUI("studentVideoLibrary");
                     else openPopupUI(item.popup);
                     onNavItemClick();
                  }}
               >
                  <ReactSVG className="menu__icon" src={item.icon} />
                  <p className="menu__nav-text">{item.text}</p>
                  {badgeDot ? (
                     <span
                        className="menu__badge menu__badge--dot"
                        aria-hidden="true"
                     />
                  ) : badgeCount > 0 ? (
                     <span className="menu__badge" aria-hidden="true">
                        {badgeCount > 99 ? "99+" : badgeCount}
                     </span>
                  ) : null}
               </button>
            </li>
         );
      }

      return (
         <li key={`${item.text}-${i}`} className="menu__item">
            <RouterLink
               to={item.link || "#"}
               end={isRootLink(item.link)}
               className={({ isActive }) =>
                  `menu__link ${isActive ? "menu__link--active" : ""}`
               }
               onClick={onNavItemClick}
            >
               <ReactSVG className="menu__icon" src={item.icon} />
               <p className="menu__nav-text">{item.text}</p>
            </RouterLink>
         </li>
      );
   };

   // desktop rows (dacă mai ai nevoie de grid)
   const cols = 4;
   const openRows = Math.max(
      1,
      Math.ceil(((sectionLinks?.length || 0) + 1) / cols),
   );

   return (
      <header className="header">
         <div className="header__wrapper">
            <div className="header__body">
               <div className="header__top">
                  <div className="header__profil-wrapper">
                     <button
                        type="button"
                        className={
                           "header__profil" +
                           (normalizedRole === ROLES.USER
                              ? " header__profil--button"
                              : "")
                        }
                        onClick={openStudentProfile}
                        disabled={normalizedRole !== ROLES.USER}
                        aria-label={
                           normalizedRole === ROLES.USER
                              ? "Deschide profilul"
                              : undefined
                        }
                     >
                        <ReactSVG className="header__statut" src={iconSrc} />
                        <div className="header__profil-details">
                           <h1
                              title={
                                 user
                                    ? `${displayName.firstName} ${displayName.lastName}`.trim()
                                    : undefined
                              }
                           >
                              {user
                                 ? `${displayName.firstName} ${displayName.lastName}`.trim() ||
                                   "..."
                                 : "..."}
                           </h1>
                           <p title={roleLabel}>{roleLabel}</p>
                        </div>
                     </button>
                  </div>
               </div>

               <div className="header__nav">
                  <div
                     className={`header__menu menu ${mobileOpen ? "menu--open" : ""}`}
                  >
                     <nav className="menu__body" id="navbar">
                        {isMobile ? (
                           <>
                              <div
                                 className="menu__drawer"
                                 aria-hidden={!mobileOpen}
                              >
                                 <ul className="menu__drawer-settings settings">
                                    {isStudent && <SettingsButton />}
                                    <DarkModeToggle />
                                 </ul>
                                 <span className="menu__drawer-hr"></span>

                                 {Array.isArray(mobileLinks) &&
                                    mobileLinks.length > 0 && (
                                    <ul
                                       key={menuSection}
                                       className={
                                          "menu__drawer-list" + sectionClass
                                       }
                                    >
                                       {mobileLinks.map(renderItem)}
                                    </ul>
                                 )}
                              </div>

                              <ul className="menu__bar">
                                 {primaryLinks.map(renderItem)}

                                 <li className="menu__item menu__item--burger">
                                    <button
                                       type="button"
                                       className="menu__burger-btn"
                                       onClick={toggleMobile}
                                       aria-label={
                                          mobileOpen
                                             ? "Close menu"
                                             : "Open menu"
                                       }
                                       aria-expanded={mobileOpen}
                                    >
                                       {/* iconurile tale burger rămân la fel */}
                                       <svg
                                          className="menu__burger-icon menu__burger-icon--open"
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 16 16"
                                       >
                                          <path
                                             fill="currentColor"
                                             d="M1.5 3.25c0-.966.784-1.75 1.75-1.75h2.5c.966 0 1.75.784 1.75 1.75v2.5A1.75 1.75 0 0 1 5.75 7.5h-2.5A1.75 1.75 0 0 1 1.5 5.75Zm7 0c0-.966.784-1.75 1.75-1.75h2.5c.966 0 1.75.784 1.75 1.75v2.5a1.75 1.75 0 0 1-1.75 1.75h-2.5A1.75 1.75 0 0 1 8.5 5.75Zm-7 7c0-.966.784-1.75 1.75-1.75h2.5c.966 0 1.75.784 1.75 1.75v2.5a1.75 1.75 0 0 1-1.75 1.75h-2.5a1.75 1.75 0 0 1-1.75-1.75Zm7 0c0-.966.784-1.75 1.75-1.75h2.5c.966 0 1.75.784 1.75 1.75v2.5a1.75 1.75 0 0 1-1.75 1.75h-2.5a1.75 1.75 0 0 1-1.75-1.75Z"
                                          />
                                       </svg>

                                       <svg
                                          className="menu__burger-icon menu__burger-icon--close"
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 1024 1024"
                                       >
                                          <path
                                             fill="currentColor"
                                             d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504L738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512L828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496L285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512L195.2 285.696a64 64 0 0 1 0-90.496z"
                                          />
                                       </svg>
                                    </button>
                                 </li>
                              </ul>
                           </>
                        ) : (
                           <>
                              <ul
                                 key={menuSection}
                                 className={"menu__list" + sectionClass}
                                 style={{ "--openRows": openRows }}
                              >
                                 {sectionLinks.map(renderItem)}
                              </ul>

                              <div className="settings__wrapper">
                                 <ul className="header__settings settings pc">
                                    {isStudent && <SettingsButton />}
                                    <DarkModeToggle />
                                 </ul>
                              </div>
                           </>
                        )}
                     </nav>
                  </div>
               </div>
            </div>
         </div>

         {children}
      </header>
   );
};

export default Header;
